import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type {
	useIsLoomEntryPointsEnabledQuery$data,
	useIsLoomEntryPointsEnabledQuery$variables,
} from './__generated__/useIsLoomEntryPointsEnabledQuery.graphql.ts';
import { createAtlAttributionHeader } from './utils/createAtlAttributionHeader.ts';

const UseIsLoomEntryPointsEnabledQuery = graphql`
	query useIsLoomEntryPointsEnabledQuery($cloudId: ID!) {
		confluence_loomEntryPoints(cloudId: $cloudId) {
			isLoomEntryPointsEnabled
		}
	}
`;

export const useIsLoomEntryPointsEnabled = (): boolean | undefined => {
	const { accessStatus, activationId, cloudId } = useSessionData();

	const shouldSkip =
		accessStatus !== AccessStatus.LICENSED_ADMIN_ACCESS &&
		accessStatus !== AccessStatus.LICENSED_USE_ACCESS;

	const { data, error, loading } = useQueryInterop<
		useIsLoomEntryPointsEnabledQuery$data,
		useIsLoomEntryPointsEnabledQuery$variables
	>(UseIsLoomEntryPointsEnabledQuery, {
		variables: {
			cloudId,
		},
		context: {
			headers: {
				...createAtlAttributionHeader(cloudId, activationId),
			},
		},
		skip: shouldSkip || Boolean(window.__SSR_LOOM_ENTRY_POINT__),
	});

	if (window.__SSR_LOOM_ENTRY_POINT__) {
		return undefined;
	}

	if (error) {
		markErrorAsHandled(error);
		return true;
	}

	if (!loading && data) {
		return data?.confluence_loomEntryPoints?.isLoomEntryPointsEnabled;
	}
};
